import AddBoxIcon from "@mui/icons-material/AddBox";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import FeeScheduleTemplateCommandAPIService from "work/fee-schedule-templates/_api/commands/fee-schedule-template-command-api-service";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { FeeScheduleTemplateOrderParameters } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import {
  FeeScheduleTemplateLoadIssue,
  getErrorLoadingFeeScheduleTemplatesByQuery,
  getFeeScheduleTemplateQueryByInterface,
  getFeeScheduleTemplatesByQuery,
  getIsLoadingFeeScheduleTemplatesByQuery,
  getLastSuccessfulFeeScheduleTemplateResponseByInterface,
  isFeeScheduleTemplateLoadIssue,
  populateFeeScheduleTemplatesByQuery,
  resetFeeScheduleTemplatesByQueryState,
  updateFeeScheduleTemplateInterfaceQuery,
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import CreateFeeScheduleTemplateDialog from "work/fee-schedule-templates/_views/dialogs/create-fee-schedule-template-dialog";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TagChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const RowActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexWrap: "nowrap",
  width: "inherit",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const interfaceName = "FeeScheduleTemplatesPage";
const columnHelper = createMRTColumnHelper<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>();

export default function FeeSchedulesPage() {
  // Refs
  const isDirtyRef = React.useRef<boolean>(false);

  // State
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });

  // Hooks
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();
  const { openDialog, closeDialog } = useDialog();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();

  // Route
  const routeParams = useLoaderData();
  const activeTab = routeParams?.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Templates;

  // Queries
  const feeScheduleTemplatesQuery = useSelector(getFeeScheduleTemplateQueryByInterface(interfaceName));
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const feeScheduleTemplates = useSelector(getFeeScheduleTemplatesByQuery(feeScheduleTemplatesQuery));
  const lastSuccessfulResponse = useSelector(getLastSuccessfulFeeScheduleTemplateResponseByInterface(interfaceName));
  const tableTemplates = feeScheduleTemplates ?? lastSuccessfulResponse;
  const isLoadingFeeScheduleTemplates = useSelector(getIsLoadingFeeScheduleTemplatesByQuery(feeScheduleTemplatesQuery));
  const errorLoadingFeeScheduleTemplates = useSelector(
    getErrorLoadingFeeScheduleTemplatesByQuery(feeScheduleTemplatesQuery),
  );

  useEffect(() => {
    if (sessionPermissions || sessionPermissionsError || sessionPermissionsLoading) return;
    dispatch(populateSessionPermissions({ session }));
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (
      feeScheduleTemplates ||
      isLoadingFeeScheduleTemplates ||
      errorLoadingFeeScheduleTemplates ||
      !feeScheduleTemplatesQuery
    )
      return;

    dispatch(
      populateFeeScheduleTemplatesByQuery({
        session,
        query: feeScheduleTemplatesQuery,
        interfaceName,
      }),
    );
  }, [
    dispatch,
    errorLoadingFeeScheduleTemplates,
    feeScheduleTemplates,
    feeScheduleTemplatesQuery,
    isLoadingFeeScheduleTemplates,
    session,
  ]);

  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const beginCreateFeeScheduleTemplate = useCallback(() => {
    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

    openDialog({
      title: "Create Template Fee Schedule",
      MuiProps: { maxWidth: "xl" },
      isDirtyRef: isDirtyRef,
      disableSave: true,
      component: (
        <CreateFeeScheduleTemplateDialog
          entityId={session.user!.legalEntityId}
          onDirty={(dirty) => {
            isDirtyRef.current = dirty;
          }}
          onSave={() => {
            isDirtyRef.current = false;
            closeDialog();
            navigate(parentPath.pathname);
            dispatch(resetFeeScheduleTemplatesByQueryState());
          }}
        />
      ),
      onClose: () => {
        isDirtyRef.current = false;
        closeDialog();
        navigate(parentPath.pathname);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.user, dispatch]);

  const handleEditFeeScheduleTemplate = useCallback(
    (id: Guid) => {
      const template = feeScheduleTemplates?.data.find((template) => template.id?.isEqualTo(id));
      if (!template) {
        enqueueSnackbar("Fee schedule template not found", { variant: "error" });
        return;
      } else if (isFeeScheduleTemplateLoadIssue(template)) {
        enqueueSnackbar("Loading fee schedule template failed", { variant: "error" });
        return;
      }

      const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

      openDialog({
        title: "Edit Template Fee Schedule",
        MuiProps: { fullWidth: isMediumDisplaySize, maxWidth: "xl" },
        isDirty: isDirtyRef.current,
        disableSave: true,
        component: (
          <CreateFeeScheduleTemplateDialog
            entityId={session.user!.legalEntityId}
            feeSchedule={template}
            onDirty={(dirty) => {
              isDirtyRef.current = dirty;
            }}
            onSave={() => {
              isDirtyRef.current = false;
              closeDialog();
              navigate(parentPath.pathname);
              dispatch(resetFeeScheduleTemplatesByQueryState());
            }}
          />
        ),
        onClose: () => {
          isDirtyRef.current = false;
          closeDialog();
          navigate(parentPath.pathname);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feeScheduleTemplates?.data, isMediumDisplaySize, session.user, navigate, dispatch],
  );

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.legalEntityId.value || !session.user?.isCompanyManager) return;

    if (routeParams.pageTab !== activeTab) {
      navigate(`/fee-schedules/${PageTab.Templates}`);
    }

    if (routeParams.action === "create") {
      beginCreateFeeScheduleTemplate();
      return;
    }

    if (routeParams.pageTab === PageTab.Templates && routeParams.id) {
      handleEditFeeScheduleTemplate(new Guid(routeParams.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, session.user?.isCompanyManager, session.user?.legalEntityId.value]);

  const query = useMemo(() => {
    const filtering = FeeScheduleTemplateFilterParameters.fromSearchParams({ searchParams, session });
    const ordering = FeeScheduleTemplateOrderParameters.fromSearchParams(searchParams);
    const pagination = PaginationParameters.fromSearchParams(searchParams);

    return new FeeScheduleTemplateQuery({
      filterParameters: filtering,
      orderParameters: ordering,
      paginationParameters: pagination,
    });
  }, [searchParams, session]);

  useEffect(() => {
    if (feeScheduleTemplatesQuery && query.isEqualTo(feeScheduleTemplatesQuery)) return;
    dispatch(updateFeeScheduleTemplateInterfaceQuery({ query, interfaceName }));
  }, [dispatch, feeScheduleTemplatesQuery, query]);

  /**
   * Archive or Unarchive the specified fee schedule template
   * @param id fee schedule id to archive / unarchive
   * @param archive if true, archive, else unarchive
   */
  async function handleArchiveFeeScheduleTemplate(id: Guid | undefined, archive: boolean): Promise<void> {
    if (!id) {
      await Promise.reject(new Error("No fee schedule template id provided"));
      return;
    }

    const action = archive ? "Archive" : "Unarchive";

    try {
      const response = await confirm({
        title: action,
        message: `${action} fee schedule template?`,
        okButtonText: action,
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      const feeScheduleService = new FeeScheduleTemplateCommandAPIService(session);

      if (archive) await feeScheduleService.archiveFeeScheduleTemplate(id);
      else await feeScheduleService.unarchiveFeeScheduleTemplate(id);
      enqueueSnackbar(`${action} fee schedule`, { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action} fee schedule. Please try again`, {
        variant: "error",
      });
    }
  }

  /**
   * Deletes the specified fee schedule template
   * @param id The fee schedule template id
   */
  async function handleDeleteFeeScheduleTemplate(id: Guid | undefined): Promise<void> {
    if (!id) {
      await Promise.reject(new Error("No fee schedule template id provided"));
      return;
    }

    try {
      const response = await confirm({
        title: "Delete?",
        message: `Delete fee schedule template?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      const feeScheduleService = new FeeScheduleTemplateCommandAPIService(session);
      await feeScheduleService.deleteFeeScheduleTemplate(id);
      enqueueSnackbar("Deleted fee schedule", { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete fee schedule. Please try again", {
        variant: "error",
      });
    }
  }

  const renderNameColumn = useCallback((template: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) => {
    if (isFeeScheduleTemplateLoadIssue(template)) return <Icon>remove</Icon>;
    return <RowTitle>{template.name}</RowTitle>;
  }, []);

  const renderTagsColumn = useCallback((template: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) => {
    if (isFeeScheduleTemplateLoadIssue(template)) return <Icon>remove</Icon>;
    if (!template.tags.length) return <Icon>remove</Icon>;
    return (
      <div>
        {template.tags.map((tag) => (
          <TagChip key={tag.value as React.Key} label={tag.value} size="small" />
        ))}
      </div>
    );
  }, []);

  const renderCreatedDateColumn = useCallback((template: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) => {
    if (isFeeScheduleTemplateLoadIssue(template) || !template.created) return <Icon>remove</Icon>;
    return <time>{template.created?.format("MM/DD/YYYY hh:mm A")}</time>;
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor(renderNameColumn, {
        header: "Name",
        id: "name",
      }),
      columnHelper.accessor(renderTagsColumn, {
        header: "Tags",
        id: "tags",
        enableSorting: false,
      }),
      columnHelper.accessor(renderCreatedDateColumn, {
        header: "Created",
        id: "createdDate",
      }),
    ],
    [renderNameColumn, renderTagsColumn, renderCreatedDateColumn],
  );

  const tableData = useMemo(() => tableTemplates?.data ?? [], [tableTemplates?.data]);

  const tableOptions: MRT_TableOptions<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> = {
    columns,
    data: tableData,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (!row.original.id) return;
        handleEditFeeScheduleTemplate(row.original.id);
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button startIcon={<AddBoxIcon />} onClick={() => navigate(`/fee-schedules/${PageTab.Templates}/create`)}>
              Create Fee Schedule
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate(`/fee-schedules/${PageTab.Templates}/create`)}>
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Fee Schedules</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => {
                if (newValue === PageTab.Active) {
                  navigate(`/fee-schedules/${PageTab.Active}/?isActive=true`);
                } else if (newValue === PageTab.Archived) {
                  navigate(`/fee-schedules/${PageTab.Archived}/?isArchived=true`);
                } else {
                  navigate(`/fee-schedules/${newValue}/`);
                }
              }}
            >
              <Tab value={PageTab.Templates} label="Templates" />
              <Tab value={PageTab.Active} label="Active" />
              <Tab value={PageTab.Archived} label="Archived" />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => {
      if (isFeeScheduleTemplateLoadIssue(row)) return `load-issue-${row.id}`;
      return row.id?.value ?? "";
    },
    enableRowActions: activeTab !== PageTab.Active,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    renderRowActions: ({ row }) => {
      if (isFeeScheduleTemplateLoadIssue(row.original)) return null;
      return (
        <RowActionsContainer>
          {activeTab !== PageTab.Active && (
            <Tooltip title={row.original.archived ? "Un-Archive Template" : "Archive Template"}>
              <span>
                <IconButton
                  onClick={(event) => {
                    if (isFeeScheduleTemplateLoadIssue(row.original)) return;
                    event.stopPropagation();
                    if (!row.original.id) return;
                    void handleArchiveFeeScheduleTemplate(row.original.id, !row.original.archived);
                  }}
                >
                  {row.original.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {activeTab === PageTab.Templates && (
            <Tooltip title="Delete Template">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!row.original.id) return;
                    void handleDeleteFeeScheduleTemplate(row.original.id);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </RowActionsContainer>
      );
    },
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: errorLoadingFeeScheduleTemplates
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: feeScheduleTemplates?.totalElements ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: isLoadingFeeScheduleTemplates && !feeScheduleTemplates,
      pagination,
      showAlertBanner: Boolean(errorLoadingFeeScheduleTemplates),
      showProgressBars: isLoadingFeeScheduleTemplates,
      sorting,
    },
  };

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToFeeSchedules) return <NotAuthorized />;

  return <MRTNoMemo<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> options={tableOptions} />;
}
